import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
}                                 from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA }        from '@angular/material/dialog';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  defer,
  distinctUntilChanged,
  map,
  startWith,
  Subject,
  takeUntil,
}                                 from 'rxjs';
import {
  BeneficiaryType,
  FileExtension,
}                                 from '~domain/enums';
import {
  CreditorRemittanceInformationType,
  PaymentData,
  PaymentFieldsToRetain,
  PaymentFormData,
  RbDocument,
  SimpleClient,
  toBeneficiaryFormData,
  UserSettings,
}                                 from '~domain/types';
import {
  DialogButton,
  DialogButtonConfig,
  DialogConfig,
}                                 from '~shared/components';
import { RbDialogForm }           from '~shared/directives';
import { DatePipe }               from '~shared/pipes';
import { ValidatorService }       from '../core/validators';
import { MainService }            from '../main/main.service';
import {
  duplicatePaymentWarningKey,
  duplicatePaymentWarningTranslateArgs,
  getDuplicatePaymentCheckValue,
  sameDuplicatePaymentCheck,
}                                 from './duplicate-payment.utils';
import { EditBeneficiaryRequest } from './edit-beneficiary.component';
import { PaymentService }         from './payment.service';

export type AddPaymentRequest = {
  paymentFormData: PaymentFormData,
  reloadAfterSave: boolean,
  id?: string,
  documentId?: string,
  documentName?: string,
  documentExtension?: FileExtension,
  clientId?: string,
  queuedDocuments?: RbDocument[],
  predictedClient?: SimpleClient,
  userSettings: UserSettings,
}

@Component({
  selector: 'rb-payment-dialog',
  templateUrl: './payment-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [DatePipe],
})
export class PaymentDialogComponent extends RbDialogForm implements OnInit, OnDestroy {
  override form = this.fb.group({
    payment: this.fb.control<PaymentFormData>(this.request.paymentFormData),
  });
  addPaymentLoading$ = this.paymentService.paymentSaving$;
  nextDocumentLoading$ = this.paymentService.fetchingDocumentPaymentDataId$.pipe(map(id => id !== null));
  paymentDocumentType$ = this.paymentService.paymentDocumentType$;
  paymentDocumentUrl$ = this.paymentService.paymentDocumentUrl$;
  paymentClients$ = this.paymentService.paymentClients$;
  paymentAccounts$ = this.paymentService.paymentAccounts$;
  beneficiaries$ = combineLatest([
    this.paymentService.globalBeneficiaries$,
    this.paymentService.clientBeneficiaries$,
  ]).pipe(map(([globalBeneficiaries, clientBeneficiaries]) => globalBeneficiaries.concat(clientBeneficiaries)));
  beneficiariesLoading$ = combineLatest([
    this.paymentService.globalBeneficiariesLoading$,
    this.paymentService.clientBeneficiariesLoading$,
  ]).pipe(map(([globalBeneficiariesLoading, clientBeneficiariesLoading]) => globalBeneficiariesLoading || clientBeneficiariesLoading));
  beneficiaryEditing$ = this.paymentService.beneficiaryEditing$;
  addingAnotherPayment = false;
  paymentFieldsToRetain: PaymentFieldsToRetain = {
    from: this.request.userSettings?.paymentFieldsToRetain?.from ?? true,
    beneficiary: this.request.userSettings?.paymentFieldsToRetain?.beneficiary ?? false,
    amount: this.request.userSettings?.paymentFieldsToRetain?.amount ?? false,
    remittanceInformation: this.request.userSettings?.paymentFieldsToRetain?.remittanceInformation ?? false,
    executionDate: this.request.userSettings?.paymentFieldsToRetain?.executionDate ?? false,
    internalNote: this.request.userSettings?.paymentFieldsToRetain?.internalNote ?? false,
  };
  lastUsedButton$ = new BehaviorSubject<DialogButton | null>(null);
  nextButtonIsLoading$ = combineLatest([
    this.nextDocumentLoading$,
    this.lastUsedButton$,
  ]).pipe(
    map(([nextDocumentLoading, lastUsedButton]) => nextDocumentLoading && (lastUsedButton === DialogButton.Next)),
  );
  okButtonIsLoading$ = combineLatest([
    this.addPaymentLoading$,
    this.nextDocumentLoading$,
    this.lastUsedButton$,
  ]).pipe(
    map(([addPaymentLoading, nextDocumentLoading, lastUsedButton]) => (addPaymentLoading || nextDocumentLoading) && (lastUsedButton === DialogButton.OK)),
  );
  duplicatePaymentWarningKey$ = this.paymentService.duplicatePaymentsType$.pipe(
    map(duplicatePaymentWarningKey),
  );
  duplicatePaymentWarningTranslateArgs$ = this.paymentService.duplicatePaymentsLatestDate$.pipe(
    map((latestDate) => duplicatePaymentWarningTranslateArgs(this.datePipe, latestDate)),
  );
  buttons: DialogButtonConfig[] = [
    (this.request.queuedDocuments?.length ?? 0) === 0
      ? {
        id: DialogButton.Cancel,
        label: 'APP.BUTTON.CANCEL.LABEL',
        icon: 'cross',
        style: 'secondary',
      }
      : {
        id: DialogButton.Next,
        label: 'APP.BUTTON.SKIP.LABEL',
        icon: 'chevron-right',
        style: 'secondary',
        autoClose: false,
        isLoading$: this.nextButtonIsLoading$,
      },
    {
      id: DialogButton.OK,
      label: 'APP.BUTTON.CONFIRM.LABEL',
      icon: 'check',
      style: 'primary',
      autoClose: false,
      disabled$: this.submitDisabled$,
      isLoading$: this.okButtonIsLoading$,
    },
  ];
  dialogConfig: DialogConfig = {
    title: this.request.id
      ? 'APP.EDIT_PAYMENT_DIALOG.TITLE'
      : (this.request.documentId ? 'APP.DOCUMENT_PAYMENT_DIALOG.TITLE' : 'APP.ADD_PAYMENT_DIALOG.TITLE'),
    beta: !!this.request.documentId,
    closeable: true,
    maximizable: !!this.request.documentId,
    errorKey$: this.errorKey$,
    errorContext: 'APP.ADD_PAYMENT_DIALOG.ERROR',
    warningKey$: this.duplicatePaymentWarningKey$,
    warningContext: 'APP.ADD_PAYMENT_DIALOG.WARNING',
    warningTranslateArgs$: this.duplicatePaymentWarningTranslateArgs$,
    queuedItems: this.request.queuedDocuments?.length,
    buttons: this.buttons,
  };
  dialogMaximized: boolean = false;
  private readonly documentNameEditingErrorKey = 'document_name_editing';
  private readonly documentNameEditing$ = new BehaviorSubject(false);
  private destroy$ = new Subject<void>();

  constructor(
    protected override readonly fb: NonNullableFormBuilder,
    protected override readonly validatorService: ValidatorService,
    private readonly datePipe: DatePipe,
    private readonly mainService: MainService,
    private readonly paymentService: PaymentService,
    @Inject(MAT_DIALOG_DATA) public request: AddPaymentRequest) {
    super(fb, validatorService);
  }

  get docViewerMaxHeight() {
    return this.dialogMaximized ? 'calc(100vh - 18.2rem)' : 'min(70.1rem, calc(100vh - 18.2rem))';
  }

  override get errorKey$() {
    return combineLatest([super.errorKey$, defer(() => this.documentNameEditing$)]).pipe(
      map(([errorKey, documentNameEditing]) => errorKey ?? (documentNameEditing ? this.documentNameEditingErrorKey : null)),
      distinctUntilChanged(),
    );
  }

  override get submitDisabled$() {
    return combineLatest([super.submitDisabled$, defer(() => this.documentNameEditing$)]).pipe(
      map(([submitDisabled, documentNameEditing]) => submitDisabled || documentNameEditing),
      distinctUntilChanged(),
    );
  }

  private get documentNameEditing() {
    return this.documentNameEditing$.value;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    if (this.request.documentId && this.request.userSettings.dialogMaximized) {
      this.dialogMaximized = true;
    }

    this.addPaymentLoading$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(loading => {
        if (this.addingAnotherPayment && !loading) {
          const payment = this.form.controls.payment.value;
          const retainedBeneficiary = this.paymentFieldsToRetain.beneficiary && payment.beneficiary
            ? {
              ...payment.beneficiary,
              ...(!payment.beneficiary.global ? { id: null, singleUse: true, type: BeneficiaryType.BENEFICIARY } : {}),
            }
            : null;

          if (!this.paymentFieldsToRetain.from) {
            this.paymentService.fetchClientBeneficiaries('');
          }

          this.form.controls.payment.setValue({
            clientId: this.paymentFieldsToRetain.from ? payment.clientId : '',
            accountId: this.paymentFieldsToRetain.from ? payment.accountId : '',
            amount: this.paymentFieldsToRetain.amount ? payment.amount : null,
            executionDate: this.paymentFieldsToRetain.executionDate ? payment.executionDate : null,
            remittanceInformation: this.paymentFieldsToRetain.remittanceInformation ? payment.remittanceInformation : '',
            internalNote: this.paymentFieldsToRetain.internalNote ? payment.internalNote : null,
            remittanceInformationType: this.paymentFieldsToRetain.remittanceInformation
              ? payment.remittanceInformationType
              : CreditorRemittanceInformationType.unstructured,
            beneficiary: retainedBeneficiary,
            anotherPayment: true,
            editBeneficiary: retainedBeneficiary ? toBeneficiaryFormData(retainedBeneficiary) : null,
          }, { emitEvent: false });
          this.paymentService.clearDuplicatePayments();
          this.addingAnotherPayment = false;
        }
      });

    this.form.controls.payment.valueChanges.pipe(
      startWith(this.form.controls.payment.value),
      map((payment) => getDuplicatePaymentCheckValue(payment, { request: { id: this.request.id } })),
      debounceTime(500),
      distinctUntilChanged(sameDuplicatePaymentCheck),
      takeUntil(this.destroy$),
    ).subscribe((value) => {
      if (!value) {
        this.paymentService.clearDuplicatePayments();
      } else {
        this.paymentService.checkDuplicatePayments(value.clientId, value.request);
      }
    });
  }

  fetchClientBeneficiaries(clientId: string) {
    this.paymentService.fetchClientBeneficiaries(clientId);
  }

  onDocumentNameRenamingChange(renaming: boolean) {
    this.documentNameEditing$.next(renaming);
    this.form.updateValueAndValidity();
  }

  saveBeneficiary(editBeneficiaryRequest: EditBeneficiaryRequest) {
    const { beneficiary, clientId } = editBeneficiaryRequest;
    if (beneficiary.id !== null) {
      if (beneficiary.singleUse) {
        this.paymentService.deleteBeneficiary(beneficiary.id, clientId);
      } else {
        this.paymentService.updateBeneficiary(beneficiary.id, clientId, beneficiary);
      }
    } else if (!beneficiary.singleUse) {
      this.paymentService.addBeneficiary(clientId, beneficiary);
    }
  }

  onPaymentFieldsToRetainChange(paymentFieldsToRetain: PaymentFieldsToRetain) {
    this.paymentFieldsToRetain = paymentFieldsToRetain;
  }

  onDialogButtonClick(dialogButton: DialogButton) {
    if (dialogButton === DialogButton.OK && this.form.valid && !this.documentNameEditing) {
      this.form.markAllAsTouched();
      const paymentFormData: PaymentData = {
        id: this.request.id,
        documentId: this.request.documentId,
        clientId: this.form.controls.payment.value.clientId,
        accountId: this.form.controls.payment.value.accountId,
        remittanceInformation: this.form.controls.payment.value.remittanceInformation,
        internalNote: this.form.controls.payment.value.internalNote,
        remittanceInformationType: this.form.controls.payment.value.remittanceInformationType,
        counterpartName: this.form.controls.payment.value.beneficiary?.counterpartName,
        counterpartReference: this.form.controls.payment.value.beneficiary?.counterpartReference,
        executionDate: this.form.controls.payment.value.executionDate,
        amount: this.form.controls.payment.value.amount!,
      };
      this.paymentService.savePayment(
        paymentFormData,
        this.form.controls.payment.value.anotherPayment,
        this.form.controls.payment.value.anotherPayment ? false : this.request.reloadAfterSave,
        this.request.queuedDocuments,
      );
      if (this.form.controls.payment.value.anotherPayment) {
        this.mainService.setUserSettings({ paymentFieldsToRetain: this.paymentFieldsToRetain });
      }
      this.lastUsedButton$.next(DialogButton.OK);
      this.addingAnotherPayment = this.form.controls.payment.value.anotherPayment;
      this.paymentService.clearDuplicatePayments();
    }

    if (dialogButton === DialogButton.Next) {
      if (this.request.queuedDocuments) {
        const qDocs = [...this.request.queuedDocuments];
        const doc = qDocs.shift();
        if (doc) {
          this.lastUsedButton$.next(DialogButton.Next);
          this.paymentService.fetchNextDocumentPaymentData(doc, qDocs, this.request.reloadAfterSave);
          this.paymentService.clearDuplicatePayments();
        }
      }
    }

    if (dialogButton === DialogButton.Cancel) {
      this.onClose();
    }
  }

  onClose() {
    if (this.request.documentId) {
      this.paymentService.clearPaymentDocument();
    }
    this.paymentService.clearDuplicatePayments();
    this.paymentService.reloadPage();
  }

  onMaximizedChange(newValue: boolean) {
    this.mainService.setUserSettings({ dialogMaximized: newValue });
  }
}
